import { useEffect, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import PermissionMatrix from "./PermissionMatrix";

import {
  createRole,
  updateRole,
  assignPermissions,
} from "services/api/roleApi";

import { getPermissions } from "services/api/permissionApi";

import {
  showSuccess,
  showError,
} from "utils/toast";

export default function RoleModal({
  open,
  onClose,
  reload,
  item,
}) {
  const isEdit = Boolean(item);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [loading, setLoading] = useState(false);

  //----------------------------------
  // Generate Slug
  //----------------------------------

  const generateSlug = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_");

  //----------------------------------
  // Load Permissions
  //----------------------------------

  const loadPermissions = async () => {
    try {
      const response = await getPermissions();

      const data =
        response?.data?.data?.permissions ||
        response?.data?.permissions ||
        response?.permissions ||
        response ||
        [];

      setPermissions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setPermissions([]);
    }
  };

  //----------------------------------
  // Initialize Modal
  //----------------------------------

  useEffect(() => {
    if (!open) return;

    loadPermissions();

    if (isEdit) {
      setName(item.name || "");
      setSlug(item.slug || "");

      setSelectedPermissions(
        item.permissions?.map(
          (permission) => permission.slug
        ) || []
      );
    } else {
      setName("");
      setSlug("");
      setSelectedPermissions([]);
    }
  }, [open, item, isEdit]);

  //----------------------------------
  // Submit
  //----------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      return showError("Role name is required.");
    }

    if (!slug.trim()) {
      return showError("Slug is required.");
    }

    try {
      setLoading(true);

      let roleId;

      if (isEdit) {
        await updateRole(item.id, {
          name,
          slug,
        });

        roleId = item.id;
      } else {
        const response = await createRole({
          name,
          slug,
        });

        roleId =
          response?.data?.data?.id ||
          response?.data?.id ||
          response?.data?.role?.id ||
          response?.id;
      }

      await assignPermissions(
        roleId,
        selectedPermissions
      );

      showSuccess(
        isEdit
          ? "Role updated successfully."
          : "Role created successfully."
      );

      reload();

      onClose();
    } catch (error) {
      console.error(error);

      showError(
        error.response?.data?.message ||
          "Something went wrong."
      );
    } finally {
      setLoading(false);
    }
  };

  //----------------------------------

  if (!open) return null;

  return (
    <CrudModal
      open={open}
      title={
        isEdit
          ? "Edit Role"
          : "Create Role"
      }
      loading={loading}
      submitText={
        isEdit
          ? "Update Role"
          : "Create Role"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit}>
        {/* Role Name */}

        <div className="mb-5">
          <label className="mb-2 block font-medium">
            Role Name{" "}
            <span className="text-red-500">
              *
            </span>
          </label>

          <input
            type="text"
            value={name}
            placeholder="Enter Role Name"
            className="w-full rounded-lg border p-3 outline-none focus:border-brand-500"
            onChange={(e) => {
              const value = e.target.value;

              setName(value);
              setSlug(generateSlug(value));
            }}
          />
        </div>

        {/* Slug */}

        <div className="mb-6">
          <label className="mb-2 block font-medium">
            Slug
          </label>

          <input
            type="text"
            value={slug}
            readOnly
            className="w-full cursor-not-allowed rounded-lg border bg-gray-100 p-3 text-gray-500"
          />

          <p className="mt-1 text-xs text-gray-500">
            Slug is generated automatically.
          </p>
        </div>

        {/* Permission Matrix */}

        <PermissionMatrix
          permissions={permissions}
          selectedPermissions={
            selectedPermissions
          }
          setSelectedPermissions={
            setSelectedPermissions
          }
        />
      </form>
    </CrudModal>
  );
}